import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { VisitCard } from "../components/VisitCard";
import { Button } from "../components/ui/Button";
import { Card, CardBody } from "../components/ui/Card";
import { api, ApiException } from "../lib/api";
import { cn } from "../lib/cn";

interface VisitItem {
  id: string;
  title: string | null;
  provider_name_snapshot: string | null;
  provider_specialty_snapshot: string | null;
  facility_name_snapshot: string | null;
  starts_at: string;
  arrive_lead_minutes: number;
  visit_category: string | null;
  status: "open" | "accepted" | "cancelled" | "completed";
  ride_required: number;
  primary_acceptor_id: string | null;
}

interface MeResponse {
  user: { id: string } | null;
  circles: Array<{ id: string; role: "admin" | "member" }>;
}

type Filter = "all" | "upcoming" | "past";

const CT = "America/Chicago";

function dayKey(iso: string): string {
  // YYYY-MM-DD in CT (so groupings match the visit's local day).
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CT,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
  return parts;
}

function todayKey(): string {
  return dayKey(new Date().toISOString());
}

function tomorrowKey(): string {
  return dayKey(new Date(Date.now() + 86_400_000).toISOString());
}

function prettyDayLabel(key: string): string {
  const t = todayKey();
  const tm = tomorrowKey();
  if (key === t) return "Today";
  if (key === tm) return "Tomorrow";
  // Convert "YYYY-MM-DD" to a labeled Date for nicer formatting.
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!, 12)); // mid-day to dodge TZ rollover
  return new Intl.DateTimeFormat("en-US", {
    timeZone: CT,
    weekday: "long",
    month: "long",
    day: "numeric",
    year: dt.getUTCFullYear() === new Date().getUTCFullYear() ? undefined : "numeric",
  }).format(dt);
}

export function CalendarView() {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [visits, setVisits] = useState<VisitItem[] | null>(null);
  const [filter, setFilter] = useState<Filter>("upcoming");

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get<MeResponse>("/api/me");
        if (!meRes.user) return navigate("/signin", { replace: true });
        if (meRes.circles.length === 0) return navigate("/app", { replace: true });
        setMe(meRes);
        const r = await api.get<{ visits: VisitItem[] }>(`/api/circles/${meRes.circles[0]!.id}/visits`);
        setVisits(r.visits);
      } catch (err) {
        if (err instanceof ApiException && err.status === 401) navigate("/signin", { replace: true });
      }
    })();
  }, [navigate]);

  const filtered = useMemo(() => {
    if (!visits) return null;
    const now = Date.now();
    let list: VisitItem[] = visits;
    if (filter === "upcoming") list = visits.filter((v) => new Date(v.starts_at).getTime() >= now - 3_600_000); // include "today still"
    if (filter === "past") list = visits.filter((v) => new Date(v.starts_at).getTime() < now);
    // Sort: upcoming ascending, past descending.
    if (filter === "past") {
      list = [...list].sort((a, b) => +new Date(b.starts_at) - +new Date(a.starts_at));
    } else {
      list = [...list].sort((a, b) => +new Date(a.starts_at) - +new Date(b.starts_at));
    }
    return list;
  }, [visits, filter]);

  const grouped = useMemo(() => {
    if (!filtered) return null;
    const map = new Map<string, VisitItem[]>();
    for (const v of filtered) {
      const k = dayKey(v.starts_at);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(v);
    }
    return [...map.entries()];
  }, [filtered]);

  const isAdmin = me?.circles[0]?.role === "admin";

  return (
    <AppShell activeTab="calendar">
      <div className="mx-auto max-w-3xl px-5 py-6 sm:py-8">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">Calendar</h1>
          {isAdmin && (
            <Link to="/app/visits/new">
              <Button>+ New visit</Button>
            </Link>
          )}
        </div>

        <div className="mt-4 inline-flex p-1 bg-surface-2 rounded-md">
          {(["upcoming", "all", "past"] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                "px-4 py-1.5 rounded text-sm font-medium",
                filter === f ? "bg-surface text-text shadow-sm" : "text-text-muted hover:text-text",
              )}
            >
              {f === "upcoming" ? "Upcoming" : f === "past" ? "Past" : "All"}
            </button>
          ))}
        </div>

        <div className="mt-5">
          {grouped === null && <div className="text-text-muted">Loading…</div>}
          {grouped && grouped.length === 0 && (
            <Card>
              <CardBody>
                <h2 className="text-lg font-semibold">No visits</h2>
                <p className="mt-1 text-text-muted">
                  {filter === "upcoming"
                    ? "Nothing on the calendar yet."
                    : filter === "past"
                    ? "No past visits to look back on."
                    : "No visits in this circle yet."}
                </p>
              </CardBody>
            </Card>
          )}
          {grouped?.map(([day, items]) => (
            <section key={day} className="mb-8">
              <h2 className="sticky top-14 sm:top-0 z-10 bg-bg/85 backdrop-blur py-2 -mx-5 px-5 text-sm font-semibold text-text-muted uppercase tracking-wide">
                {prettyDayLabel(day)}
              </h2>
              <div className="mt-2 space-y-3">
                {items.map((v) => (
                  <VisitCard key={v.id} visit={v} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
